import { message } from "antd";
import React, { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useDisclosure } from "../../../../../../../hooks/useDisclosure";
import { AppButton } from "../../../../../../atoms/Button";
import { DecimalUtils } from "../../../../../../libs/utils";
import { useBookingSelector } from "../../../../hooks/useBookingSelector";
import { useTorRules } from "../../../../hooks/useTorRules";
import type { BillingSchedule, GetBillingScheduleResult } from "../api";
import { useGetBillingSchedule } from "../api";
import { useBreakdownStaleContext } from "../BreakdownStaleContext";
import { BillingSchedulePreviewModal } from "./BillingSchedulePreviewModal";
import { getBillingScheduleRentSumErrorMessage, isBillingScheduleRentSumValid } from "./rentSumValidation";
import type { BreakdownFormValues } from "./types";

type BillingSchedulePreviewSectionProps = {
  rows: BillingSchedule[];
  hasValidTorType?: boolean;
  annualRent?: number;
  yearNumber: number;
  maxInstallments: number;
  maxTorPeriod: number;
  isInstallmentDisabled?: boolean;
};

export const BillingSchedulePreviewSection = (props: BillingSchedulePreviewSectionProps) => {
  const { rows, hasValidTorType, annualRent, yearNumber, maxInstallments, maxTorPeriod, isInstallmentDisabled } = props;
  const { assertNoStaleCommercialBreakdowns } = useBreakdownStaleContext();

  const { leaseId } = useBookingSelector();
  const { isTurnoverRentOnly } = useTorRules();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { trigger, control } = useFormContext<BreakdownFormValues>();

  const watchedRows = useWatch({ control, name: "rows" });

  const [billingSchedulePreviewData, setBillingSchedulePreviewData] = useState<GetBillingScheduleResult>({
    baseRentPaymentSchedule: [],
    torschedule: [],
  });

  const { getBillingSchedule, isPendingGetBillingSchedule } = useGetBillingSchedule({
    onSuccess: (data) => {
      setBillingSchedulePreviewData(data);
      onOpen();
    },
    onError: () => message.error("Failed to load billing schedule preview"),
  });

  const validateForm = async (): Promise<boolean> => {
    const isValid = await trigger();
    if (!isValid) return false;

    const hasAllSequenceValues = (
      field: "assignedInstallmentNo" | "assignedTorPeriodNo",
      maxValue: number,
    ): boolean => {
      const assignedValues = new Set(
        (watchedRows ?? [])
          .map((row) => row?.[field]?.value)
          .filter((value): value is number => typeof value === "number" && value > 0),
      );

      for (let value = 1; value <= maxValue; value++) {
        if (!assignedValues.has(value)) return false;
      }
      return true;
    };

    if (!isTurnoverRentOnly && !hasAllSequenceValues("assignedInstallmentNo", maxInstallments)) {
      message.error("Assign at least one row to each installment number.");
      return false;
    }

    if (hasValidTorType && !hasAllSequenceValues("assignedTorPeriodNo", maxTorPeriod)) {
      message.error("Assign at least one row to each TOR period number.");
      return false;
    }

    if (!isBillingScheduleRentSumValid(watchedRows, annualRent)) {
      message.error(getBillingScheduleRentSumErrorMessage(yearNumber));
      return false;
    }

    return true;
  };

  const buildBillingSchedulePayload = (): BillingSchedule[] =>
    rows.map((row, idx) => {
      const baseRentInput = watchedRows?.[idx]?.baseRent ?? row?.baseRent;
      const vatRate = watchedRows?.[idx]?._vatRate ?? 0;
      const vatAmount = DecimalUtils.multiply(baseRentInput, vatRate);
      const newBaseRent = DecimalUtils.toNumber(baseRentInput);
      const newVat = DecimalUtils.toNumber(vatAmount);
      const newTotal = DecimalUtils.toNumber(DecimalUtils.add([baseRentInput, vatAmount]));
      const assignedInstallment = watchedRows?.[idx]?.assignedInstallmentNo?.value;
      return {
        baseRent: newBaseRent,
        days: row?.days,
        endDate: row?.endDate,
        installment: isInstallmentDisabled
          ? (row.installment ?? assignedInstallment ?? 0)
          : (assignedInstallment ?? row.installment),
        noOfYear: row?.noOfYear,
        startDate: row?.startDate,
        ...(hasValidTorType ? { torPeriod: watchedRows?.[idx]?.assignedTorPeriodNo?.value } : {}),
        total: newTotal,
        vat: newVat,
        vatInPercentage: row?.vatInPercentage,
      };
    });

  const handlePreview = async () => {
    if (!leaseId) {
      message.error("Unable to find lease ID");
      return;
    }

    const isValid = await validateForm();
    if (!isValid) return;

    if (!assertNoStaleCommercialBreakdowns()) return;

    const commercialBillingSchedule = buildBillingSchedulePayload();
    await getBillingSchedule({ leaseId, commercialBillingSchedule });
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <AppButton variant="subtle" loading={isPendingGetBillingSchedule} onClick={handlePreview}>
          Preview Billing Schedule
        </AppButton>
      </div>

      <BillingSchedulePreviewModal
        isOpen={isOpen}
        onClose={onClose}
        billingSchedulePreviewData={billingSchedulePreviewData}
        yearNumber={yearNumber}
        hasValidTorType={hasValidTorType}
      />
    </>
  );
};
